// Opens the side-menu detail panel in a new browser tab by writing a
// serializable snapshot of the panel's props into localStorage under a
// unique key, then routing to /result-details/:dbkey?snapshot=<key>. The
// new tab boots the full SPA and mounts ResultTableRecDetail directly so
// chart clicks, expand/collapse, and other interactions work as in the
// originating panel.
//
// Cross-tab annotation writes and search-term highlighting are v1 gaps;
// see ResultDetailsNewPage.js for details.

const SNAPSHOT_PREFIX = 'gqls:detail:';

const makeSnapshotKey = () => {
    const rand = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return `${SNAPSHOT_PREFIX}${rand}`;
};

const serializeSnapshot = (selectedItem, data, fieldConfig, extras = {}) => {
    // fieldConfig is a module-level singleton whose methods can't be
    // serialized. We only persist the data fields the new tab will
    // rehydrate into the imported FieldConfig module.
    const fieldConfigState = {
        fieldConfig: fieldConfig?.fieldConfig,
        combinedFields: fieldConfig?.combinedFields,
        userTableTemplate: fieldConfig?.userTableTemplate
    };

    // Capture the global API call context so backend fetches (e.g., the
    // Subject Hit Landscape) work in the new tab. window.$javaCall holds
    // workflowId / userId / gqApiKey plus any active filter state.
    const javaCall = (typeof window !== 'undefined' && window.$javaCall)
        ? window.$javaCall
        : undefined;

    // `filter`, `annotState`, etc. live as separate props alongside `data`
    // on the side panel — not inside `data`. We read them from `extras`
    // (the rich preferred path) and fall back to `data.*` for callers that
    // haven't been updated yet.
    const pick = (key) => (extras[key] !== undefined ? extras[key] : data?.[key]);
    const filterSrc = extras.filter !== undefined ? extras.filter : data?.filter;

    return JSON.stringify({
        item: selectedItem,
        data,
        annotState: pick('annotState'),
        parentAnnotState: pick('parentAnnotState'),
        filter: filterSrc ? { state: filterSrc.state } : undefined,
        displayedColumns: pick('displayedColumns'),
        isAbWorkflow: pick('isAbWorkflow'),
        fromItemsPage: pick('fromItemsPage'),
        groupValue1: pick('groupValue1'),
        groupValue2: pick('groupValue2'),
        isCmNoSeq: pick('isCmNoSeq'),
        idx: extras.idx !== undefined ? extras.idx : data?.idx,
        selectedQueryId: pick('selectedQueryId'),
        fieldConfigState,
        javaCall
    });
};

export const openDetailsInNewPage = (selectedItem, data, fieldConfig, _toggle, extras) => {
    if (!selectedItem || !selectedItem.dbkey) {
        console.warn('openDetailsInNewPage: missing selectedItem.dbkey');
        return;
    }

    try {
        Object.keys(localStorage)
            .filter(k => k.startsWith(SNAPSHOT_PREFIX))
            .forEach(k => localStorage.removeItem(k));
    } catch (e) { /* ignore stale cleanup errors */ }

    const snapshotKey = makeSnapshotKey();
    const snapshot = JSON.parse(serializeSnapshot(selectedItem, data, fieldConfig, extras));

    // Primary: in-memory map — new tab reads via window.opener, no quota issues.
    window.__detailSnapshots = window.__detailSnapshots || new Map();
    window.__detailSnapshots.set(snapshotKey, snapshot);

    // Best-effort localStorage backup for when the opener tab is closed before
    // the new tab finishes loading. Silently skip on quota errors.
    try {
        localStorage.setItem(snapshotKey, JSON.stringify(snapshot));
    } catch (e) { /* quota exceeded — opener path covers it */ }

    // HashRouter URL — keep in sync with the route registered in routes/index.js.
    const url = `${window.location.origin}${window.location.pathname}#/result-details/${encodeURIComponent(selectedItem.dbkey)}?snapshot=${encodeURIComponent(snapshotKey)}`;

    const newWindow = window.open(url, '_blank');
    if (!newWindow) {
        // Pop-up blocked — clean up so we don't leak the entries.
        try { localStorage.removeItem(snapshotKey); } catch (e) { /* ignore */ }
        window.__detailSnapshots.delete(snapshotKey);
        alert('Unable to open new window. Please check your browser popup settings.');
    }
};
